const capabilities = [
  {
    icon: 'ri-shield-fill',
    title: 'Paint Protection Film',
    desc: 'XPEL-certified PPF for full vehicles, partial front-end, and specialty applications including motorcycles and exotics.',
  },
  {
    icon: 'ri-sparkling-2-fill',
    title: 'Ceramic Coating',
    desc: 'Professional 9H-rated ceramic coating systems from Ceramic Pro and XPEL Fusion. Available in multi-year durability tiers.',
  },
  {
    icon: 'ri-sun-fill',
    title: 'Window Tint',
    desc: 'Premium automotive-grade ceramic window film. All installations are CT-legal and backed by lifetime warranties.',
  },
  {
    icon: 'ri-paint-fill',
    title: 'Matte & Color Change PPF',
    desc: 'Full vehicle color transformation using premium matte and satin PPF — protecting factory paint while creating a custom look.',
  },
  {
    icon: 'ri-eye-fill',
    title: 'Windshield Protection',
    desc: 'XPEL windshield film that absorbs chip impacts and dramatically reduces the risk of costly windshield replacements.',
  },
  {
    icon: 'ri-tools-fill',
    title: 'Paint Correction',
    desc: 'Multi-stage paint correction removes swirl marks, scratches, and holograms — essential prep before any PPF or coating.',
  },
];

const values = [
  {
    number: '01',
    title: 'Precision Over Speed',
    desc: 'We take the time to do it right. Every edge, every seam, every panel receives the same obsessive attention to detail — regardless of vehicle or package.',
  },
  {
    number: '02',
    title: 'Premium Materials Only',
    desc: 'We exclusively install XPEL, SunTek, and Ceramic Pro products. We have never and will never install budget or unknown films to cut costs.',
  },
  {
    number: '03',
    title: 'Transparent Pricing',
    desc: 'Every quote is itemized and explained. No hidden fees. No surprises. You know exactly what you\'re getting before we begin — every time.',
  },
  {
    number: '04',
    title: 'Passionate About Cars',
    desc: 'We don\'t just protect vehicles — we love them. Every member of our team is a genuine automotive enthusiast who understands what your car means to you.',
  },
];

export default function AboutCapabilities() {
  return (
    <section className="bg-[#060606] py-28" id="capabilities">
      <div className="max-w-7xl mx-auto px-6">

        {/* AI Answer Block */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-20">
          <div className="bg-[#111111] border border-white/10 rounded-2xl p-8">
            <div className="w-10 h-10 flex items-center justify-center bg-[#FF6B35]/10 rounded-xl mb-5">
              <i className="ri-search-eye-line text-[#FF6B35] text-xl" />
            </div>
            <h2 className="text-white font-black text-2xl mb-3">What does DBN Garage do?</h2>
            <p className="text-gray-400 text-sm leading-relaxed mb-4">
              DBN Garage is South Windsor, CT's premier automotive protection and enhancement studio. We install XPEL paint protection film, ceramic coatings, window tint, matte PPF, windshield protection, and motorcycle protection — serving all of Hartford County with 25+ years of professional expertise.
            </p>
            <p className="text-gray-400 text-sm leading-relaxed">
              We are an XPEL-certified installer using only the world's top protection brands: XPEL, SunTek, and Ceramic Pro. Every installation is backed by manufacturer warranties and our own craftsmanship guarantee.
            </p>
          </div>
          <div className="bg-[#111111] border border-white/10 rounded-2xl p-8">
            <div className="w-10 h-10 flex items-center justify-center bg-[#FF6B35]/10 rounded-xl mb-5">
              <i className="ri-vip-diamond-fill text-[#FF6B35] text-xl" />
            </div>
            <h2 className="text-white font-black text-2xl mb-3">Why choose a certified installer?</h2>
            <p className="text-gray-400 text-sm leading-relaxed mb-4">
              XPEL certification requires passing rigorous installation testing and ongoing training. Certified installers have access to premium film libraries, dedicated technical support, and direct warranty fulfillment. This protects you as a customer — your warranty is with the manufacturer, not just a local shop.
            </p>
            <p className="text-gray-400 text-sm leading-relaxed">
              DBN Garage is one of a select number of XPEL-certified shops in Connecticut. That designation is earned, not purchased — and it means every film we touch is installed to factory specification.
            </p>
          </div>
        </div>

        {/* Capabilities grid */}
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-12">
          <div>
            <span className="text-[#FF6B35] text-xs font-black uppercase tracking-[0.2em] mb-3 flex items-center gap-2">
              <span className="w-6 h-px bg-[#FF6B35]" />
              What We Do
            </span>
            <h2 className="font-display font-black text-4xl lg:text-5xl text-white leading-tight">
              Complete Auto
              <br />
              <span className="text-[#FF6B35]">Protection Capabilities</span>
            </h2>
          </div>
          <p className="text-gray-400 text-base max-w-sm leading-relaxed">
            From the clearest PPF to the deepest ceramic gloss — every service in our studio is delivered at the highest level.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-20">
          {capabilities.map((cap) => (
            <div key={cap.title} className="bg-[#111111] border border-white/8 rounded-2xl p-6 hover:border-[#FF6B35]/30 transition-all duration-300 group">
              <div className="w-11 h-11 flex items-center justify-center bg-[#FF6B35]/10 rounded-xl mb-4">
                <i className={`${cap.icon} text-[#FF6B35] text-2xl`} />
              </div>
              <h3 className="font-bold text-lg text-white mb-2">{cap.title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{cap.desc}</p>
            </div>
          ))}
        </div>

        {/* Values */}
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-12">
          <div>
            <span className="text-[#FF6B35] text-xs font-black uppercase tracking-[0.2em] mb-3 flex items-center gap-2">
              <span className="w-6 h-px bg-[#FF6B35]" />
              Our Values
            </span>
            <h2 className="font-display font-black text-4xl lg:text-5xl text-white leading-tight">
              How We Work,
              <br />
              <span className="text-[#FF6B35]">Every Single Time</span>
            </h2>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-16">
          {values.map((v) => (
            <div key={v.number} className="bg-[#0E0E0E] border border-white/8 rounded-2xl p-7 flex gap-5">
              <div className="flex-shrink-0">
                <span className="font-black text-3xl text-[#FF6B35]/30 leading-none">{v.number}</span>
              </div>
              <div>
                <h3 className="font-bold text-lg text-white mb-2">{v.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{v.desc}</p>
              </div>
            </div>
          ))}
        </div>

        {/* CTA row */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <a href="#lead-form"
            className="flex items-center gap-2 bg-[#FF6B35] hover:bg-[#E5501A] text-white font-bold px-8 py-4 rounded-xl transition-colors whitespace-nowrap cursor-pointer">
            <span className="w-5 h-5 flex items-center justify-center"><i className="ri-calendar-check-fill" /></span>
            Book a Free Consultation
          </a>
          <a href="tel:+18605550127"
            className="flex items-center gap-2 border border-white/20 hover:border-white/50 text-white font-bold px-8 py-4 rounded-xl transition-colors whitespace-nowrap cursor-pointer">
            <span className="w-5 h-5 flex items-center justify-center"><i className="ri-phone-fill" /></span>
            Call (860) 555-0127
          </a>
        </div>
      </div>
    </section>
  );
}
